/**
 * Score write-back on close (roadmap 1C, V-5).
 *
 * When a scoring session closes, the workflow's attribute write-back setting
 * (`resolveEffectiveWorkflowConfig().attributeWriteBack`, canon Doc 05 §10.1)
 * says what happens to each item's per-criterion scores:
 *
 * - `none`         — nothing.
 * - `session_only` — each score is saved as this session's value (and, like
 *                    every value typed in a session, offered up as a
 *                    contribution). A person without accept rights saves the
 *                    contribution only.
 * - `direct`       — each score is offered as a contribution (a proposed value)
 *                    that someone who approves values can accept as official.
 *                    The official value is never overwritten here (owner
 *                    ruling 2026-10-14).
 *
 * Every save goes through the one session-entry path (`saveSessionValue`,
 * src/lib/sessionValueEntry.ts). Nothing is swallowed: the result carries a
 * plain line for the person who closed the session, including what failed.
 *
 * Only scores whose key is a workspace attribute are written — a tool's own
 * bookkeeping (pairwise wins, totals) is never mistaken for a value.
 */

import type { AggregateResult, ToolResultItem } from '../types/prioritization/toolSession';
import type { AttributeWriteBack } from '../types/prioritization/workflow';
import type { ValueProposal } from '../types/prioritization/itemLifecycle';
import { saveSessionValue, type SaveSessionValueInput, type SaveSessionValueResult } from './sessionValueEntry';

/** itemId → attributeKey → score */
export type ScoresByItem = Record<string, Record<string, number>>;

function finite(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

/**
 * One item's per-criterion scores from a tool result. A criterion's own score
 * (`metadata.criterionScores[key].score`, weighted scoring) is preferred over
 * its weighted share in `scoreBreakdown`.
 */
export function perCriterionScores(result: ToolResultItem): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [key, val] of Object.entries(result.scoreBreakdown ?? {})) {
    if (finite(val)) out[key] = val;
  }
  const criterionScores = result.metadata?.criterionScores;
  if (criterionScores && typeof criterionScores === 'object') {
    for (const [key, detail] of Object.entries(criterionScores as Record<string, unknown>)) {
      const score = detail && typeof detail === 'object' ? (detail as { score?: unknown }).score : undefined;
      if (finite(score)) out[key] = score;
    }
  }
  return out;
}

/** Per-criterion scores for a one-person run (the run tab's results). */
export function scoresFromResults(results: readonly ToolResultItem[]): ScoresByItem {
  const out: ScoresByItem = {};
  for (const r of results) {
    const scores = perCriterionScores(r);
    if (Object.keys(scores).length > 0) out[r.id] = scores;
  }
  return out;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Per-criterion scores for a sitting with many people: the mean of the counted
 * answers (the aggregates already leave out removed people, late answers and
 * advisory voices).
 */
export function scoresFromAggregates(aggregates: readonly AggregateResult[]): ScoresByItem {
  const out: ScoresByItem = {};
  for (const agg of aggregates) {
    const sums: Record<string, { total: number; n: number }> = {};
    for (const pr of agg.participantResults) {
      for (const [key, val] of Object.entries(perCriterionScores(pr.result))) {
        const s = (sums[key] ??= { total: 0, n: 0 });
        s.total += val;
        s.n += 1;
      }
    }
    const means = Object.fromEntries(
      Object.entries(sums).map(([key, s]) => [key, round2(s.total / s.n)]),
    );
    if (Object.keys(means).length > 0) out[agg.itemId] = means;
  }
  return out;
}

/** Keep only scores whose key names a workspace attribute (by key, or by id mapped to its key). */
export function keepAttributeScores(
  scores: ScoresByItem,
  attributes: ReadonlyArray<{ id?: string; key: string }>,
): ScoresByItem {
  const byKey = new Set(attributes.map((a) => a.key));
  const keyById = new Map(attributes.filter((a) => a.id).map((a) => [a.id as string, a.key]));
  const out: ScoresByItem = {};
  for (const [itemId, row] of Object.entries(scores)) {
    const kept: Record<string, number> = {};
    for (const [k, v] of Object.entries(row)) {
      const attrKey = byKey.has(k) ? k : keyById.get(k);
      if (attrKey) kept[attrKey] = v;
    }
    if (Object.keys(kept).length > 0) out[itemId] = kept;
  }
  return out;
}

export interface WriteBackInput {
  setting: AttributeWriteBack;
  scores: ScoresByItem;
  sessionId: string;
  sessionName?: string;
  trackId?: string | null;
  userId: string;
  userName?: string | null;
  role?: string | null;
  /** values.accept — may save a value for this session. */
  mayAccept: boolean;
  /** values.propose — may offer a contribution. */
  mayContribute: boolean;
  /** Proposals already loaded, so a second close does not offer the same value twice. */
  existingProposals?: readonly ValueProposal[];
}

export interface WriteBackResult {
  setting: AttributeWriteBack;
  /** Scores there were to write (after skipping ones already offered). */
  attempted: number;
  /** Saved as this session's value. */
  savedForSession: number;
  /** Offered as a contribution waiting to be accepted as official. */
  contributed: number;
  failed: number;
  errors: string[];
  /** The plain line to show the person who closed the session; null when there is nothing to say. */
  message: string | null;
  tone: 'ok' | 'warn' | 'error';
}

function nothingWritten(setting: AttributeWriteBack): WriteBackResult {
  return { setting, attempted: 0, savedForSession: 0, contributed: 0, failed: 0, errors: [], message: null, tone: 'ok' };
}

type SaveFn = (input: SaveSessionValueInput) => Promise<SaveSessionValueResult>;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export async function writeBackSessionScores(
  input: WriteBackInput,
  save: SaveFn = saveSessionValue,
): Promise<WriteBackResult> {
  const base = nothingWritten(input.setting);
  if (input.setting === 'none') return base;

  const cells: Array<{ itemId: string; attributeKey: string; value: number }> = [];
  for (const [itemId, row] of Object.entries(input.scores)) {
    for (const [attributeKey, value] of Object.entries(row)) {
      const alreadyOffered = (input.existingProposals ?? []).some(
        (p) =>
          p.status === 'proposed' &&
          p.sessionId === input.sessionId &&
          p.proposedBy === input.userId &&
          p.itemId === itemId &&
          p.attributeKey === attributeKey &&
          Number(p.proposedValue) === value,
      );
      if (!alreadyOffered) cells.push({ itemId, attributeKey, value });
    }
  }
  if (cells.length === 0) return base;

  const result: WriteBackResult = { ...base, attempted: cells.length };

  if (!input.mayContribute && !input.mayAccept) {
    return {
      ...result,
      failed: cells.length,
      tone: 'warn',
      message: `The scores were not saved as values: you can't enter values in this workspace. Someone who can enter values will need to add them.`,
    };
  }

  // "direct" only ever offers contributions; "session_only" also saves this
  // session's value when the person may accept.
  const mayAcceptForSession = input.setting === 'session_only' && input.mayAccept;

  for (const cell of cells) {
    const r = await save({
      itemId: cell.itemId,
      attributeKey: cell.attributeKey,
      value: cell.value,
      sessionId: input.sessionId,
      sessionName: input.sessionName,
      trackId: input.trackId,
      userId: input.userId,
      userName: input.userName,
      role: input.role ?? 'facilitator',
      mayAcceptForSession,
      acceptanceMethod: 'facilitator_override',
    });
    if (r.savedForSession) result.savedForSession += 1;
    if (r.contributed) result.contributed += 1;
    if (!r.contributed) result.failed += 1;
    if (r.error) result.errors.push(r.error);
  }

  const parts: string[] = [];
  if (input.setting === 'session_only') {
    if (result.savedForSession > 0) {
      parts.push(`${plural(result.savedForSession, 'score was', 'scores were')} saved as this session's values.`);
    }
    const onlyContributed = result.contributed - result.savedForSession;
    if (onlyContributed > 0) {
      parts.push(
        input.mayAccept
          ? `${plural(onlyContributed, 'score was', 'scores were')} saved only as your contribution.`
          : `You can't save values for this session, so ${plural(onlyContributed, 'score was', 'scores were')} saved as your contribution.`,
      );
    }
    if (result.contributed > 0) {
      parts.push(`${plural(result.contributed, 'is', 'are')} waiting to be accepted as official.`);
    }
  } else if (result.contributed > 0) {
    parts.push(
      `${plural(result.contributed, 'score was', 'scores were')} offered as contributions, waiting to be accepted as official. The official values did not change.`,
    );
  }
  if (result.failed > 0) {
    parts.push(`${plural(result.failed, 'score was', 'scores were')} not saved: ${result.errors[0] ?? 'refused'}.`);
  }

  result.tone = result.failed > 0 ? (result.contributed > 0 ? 'warn' : 'error') : result.errors.length > 0 ? 'warn' : 'ok';
  if (result.failed === 0 && result.errors.length > 0) {
    parts.push(result.errors[0]);
  }
  result.message = parts.join(' ') || null;
  return result;
}

export interface WriteBackOnCloseInput {
  session: {
    id: string;
    name?: string;
    workspaceId?: string | null;
    cycleId?: string | null;
    trackId?: string | null;
  };
  /** One-person run: the tool's results. */
  results?: readonly ToolResultItem[];
  /** Many-person sitting: the tool session whose counted answers were just saved. */
  toolSessionId?: string;
}

/**
 * The one call every close path makes after the session's result is saved:
 * reads the write-back setting, the person's rights and the workspace's
 * attributes, then writes back. Never throws — a failure comes back as the
 * plain line to show.
 */
export async function writeBackScoresOnClose(input: WriteBackOnCloseInput): Promise<WriteBackResult> {
  const fail = (msg: string): WriteBackResult => ({
    ...nothingWritten('none'),
    errors: [msg],
    message: `The scores were not saved as values: ${msg}`,
    tone: 'error',
  });
  try {
    const [{ resolveEffectiveWorkflowConfig }, { useUserStore }, { useAttributesStore }, { useValueProposalStore }, { getCurrentWorkspaceId }] =
      await Promise.all([
        import('./workflowConfig'),
        import('../stores/prioritization/userStore'),
        import('../stores/prioritization/attributesStore'),
        import('../stores/prioritization/valueProposalStore'),
        import('./supabaseSync'),
      ]);

    let raw: ScoresByItem = {};
    if (input.results) {
      raw = scoresFromResults(input.results);
    } else if (input.toolSessionId) {
      const { useToolSessionStore } = await import('../stores/prioritization/toolSessionStore');
      raw = scoresFromAggregates(useToolSessionStore.getState().computeAggregates(input.toolSessionId));
    }
    const scores = keepAttributeScores(raw, useAttributesStore.getState().attributes ?? []);
    // Nothing scored on a workspace attribute (a pairwise run, a vote): nothing to write.
    if (Object.keys(scores).length === 0) return nothingWritten('none');

    const workspaceId = input.session.workspaceId ?? getCurrentWorkspaceId() ?? '';
    const config = await resolveEffectiveWorkflowConfig({
      workspaceId,
      cycleId: input.session.cycleId ?? null,
      trackId: input.session.trackId ?? null,
      sessionId: input.session.id,
    });
    if (config.attributeWriteBack === 'none') return nothingWritten('none');

    const user = useUserStore.getState();
    const valueStore = useValueProposalStore.getState();
    const result = await writeBackSessionScores({
      setting: config.attributeWriteBack,
      scores,
      sessionId: input.session.id,
      sessionName: input.session.name,
      trackId: input.session.trackId,
      userId: user.userId ?? '',
      userName: user.userName,
      role: user.currentRole,
      mayAccept: user.can('values.accept'),
      mayContribute: user.can('values.propose'),
      existingProposals: valueStore.proposals,
    });
    return result;
  } catch (err) {
    return fail(err instanceof Error ? err.message : 'something went wrong');
  }
}

/** What the close paths show (a toast): null when there is nothing to say. */
export function writeBackNotice(
  result: WriteBackResult,
): { type: 'success' | 'warning' | 'error'; title: string; message: string } | null {
  if (!result.message) return null;
  return {
    type: result.tone === 'ok' ? 'success' : result.tone === 'warn' ? 'warning' : 'error',
    title: 'Scores as values',
    message: result.message,
  };
}
